import {
  Box,
  Typography,
  Stack,
  Divider,
  Radio,
  Checkbox,
  Paper,
} from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import palette from '../../../../theme/palette';
import { CustomDialogBox } from '../../../../components/CustomDialogBox';
import useRefineFilter from './useRefineFilter';

function SectionHeader({
  icon, title, onSelectAll, onUnselectAll, showActions = true,
}) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Stack direction="row" alignItems="center" gap={1}>
        {icon}
        <Typography variant="subtitle1" fontWeight="bold">
          {title}
        </Typography>
      </Stack>

      {showActions && (
        <Stack direction="row" gap={1} alignItems="center">
          <Typography
            variant="caption"
            color="primary"
            sx={{ cursor: 'pointer', fontWeight: 'bold' }}
            onClick={onSelectAll}
          >
            Select All
          </Typography>

          <Typography variant="h6" color="text.secondary">
            &middot;
          </Typography>

          <Typography
            variant="caption"
            color="primary"
            sx={{ cursor: 'pointer', fontWeight: 'bold' }}
            onClick={onUnselectAll}
          >
            Unselect All
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}

function SelectableItem({
  label, selected, onClick, controlType = 'checkbox',
}) {
  const Control = controlType === 'checkbox' ? Checkbox : Radio;

  return (
    <Paper
      onClick={onClick}
      sx={{
        pr: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        cursor: 'pointer',
        border: `1px solid ${selected ? palette.primary.border : palette.grey[300]}`,
        color: selected ? 'primary.main' : 'initial',
        '&:hover': { backgroundColor: 'grey.200' },
      }}
    >
      <Control size="small" checked={selected} onChange={onClick} />
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {label}
      </Typography>
    </Paper>
  );
}

function DialogContentElement({
  crudOp,
  eventSource,
  handleOnClickEventSource,
  handleOnClickCrudOp,
  handleSelectAllOp,
  handleUnSelectAllOp,
}) {
  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {/* CRUD Section */}
      <Stack gap={1}>
        <SectionHeader
          icon={<LayersIcon color="primary" fontSize="small" />}
          title="Operation Types"
          onSelectAll={() => handleSelectAllOp('crud')}
          onUnselectAll={() => handleUnSelectAllOp('crud')}
        />

        <Typography variant="body2" color="text.secondary">
          CRUD types define the types of record operations:
          <b> Creates, Reads, Updates and Deletes </b>
          .
          Use this section to limit the types of operations that you consider as Feature usage.
        </Typography>

        <Stack direction="row" gap={1} flexWrap="wrap">
          {crudOp.map((option) => (
            <SelectableItem
              key={option.value}
              label={option.operationName}
              selected={option.selected}
              onClick={() => handleOnClickCrudOp(option.value)}
              controlType="checkbox"
            />
          ))}
        </Stack>
      </Stack>

      <Divider />

      {/* Event Sources Section */}
      <Stack gap={1}>
        <SectionHeader
          icon={<AdsClickIcon color="primary" fontSize="small" />}
          title="Operation Sources"
          showActions={false} // no select/unselect here
        />

        <Typography variant="body2" color="text.secondary">
          Custom Objects have many ways of interaction among UI Interaction, Apex Scheduled Jobs,
          Bulk API, Platform Events and others. You may select
          {' '}
          <b>UI Only</b>
          {' '}
          – to count only UI-based events,
          or
          {' '}
          <b>All Sources</b>
          {' '}
          – to involve all the other types of interactions too.
        </Typography>

        <Stack direction="row" gap={1} flexWrap="wrap">
          {eventSource.map((option) => (
            <SelectableItem
              key={option.value}
              label={option.name}
              selected={option.selected}
              onClick={() => handleOnClickEventSource(option.value)}
              controlType="radio"
            />
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}

export default function RefineFilter({
  open, handleClose, handleRefineFilterSave, customFilter,
}) {
  const {
    crudOp,
    eventSource,
    disabled,
    handleOnClickEventSource,
    handleOnClickCrudOp,
    handleSelectAllOp,
    handleUnSelectAllOp,
    handleSave,
  } = useRefineFilter({ handleRefineFilterSave, customFilter });

  return (
    <Box sx={{ minWidth: 200 }}>
      <CustomDialogBox
        title="Refine Filter"
        open={open}
        handleClose={handleClose}
        content={(
          <DialogContentElement
            crudOp={crudOp}
            eventSource={eventSource}
            handleOnClickEventSource={handleOnClickEventSource}
            handleOnClickCrudOp={handleOnClickCrudOp}
            handleSelectAllOp={handleSelectAllOp}
            handleUnSelectAllOp={handleUnSelectAllOp}
          />
        )}
        actions={{ title: 'Save', onClick: handleSave, disabled }}
      />
    </Box>
  );
}
